import React, { useEffect, useState } from 'react';
import { billingAPI } from '../api/client';
import { Card, Button, Badge } from '../components/ui';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import toast from 'react-hot-toast';

const PROVIDER_COLOR = { openai: '#10a37f', deepseek: '#4f8ef7', gemini: '#4285f4', claude: '#cc785c', grok: '#e7e7e7' };
const GATEWAY_FIELDS = {
  stripe:      [{ key: 'pub_key', label: 'Clave pública (pk_)' }, { key: 'secret_key', label: 'Clave secreta (sk_)', secret: true }, { key: 'webhook_secret', label: 'Webhook secret (whsec_)', secret: true }],
  paypal:      [{ key: 'client_id', label: 'Client ID' }, { key: 'secret', label: 'Secret', secret: true }],
  wompi:       [{ key: 'pub_key', label: 'Llave pública' }, { key: 'priv_key', label: 'Llave privada', secret: true }],
  mercadopago: [{ key: 'public_key', label: 'Public Key' }, { key: 'access_token', label: 'Access Token', secret: true }],
};

function CreditBar({ percent, size = 'md' }) {
  const color = percent >= 95 ? '#ef4444' : percent >= 85 ? '#f97316' : percent >= 70 ? '#f59e0b' : '#10b981';
  const h = size === 'sm' ? '6px' : '10px';
  return (
    <div style={{ width: '100%' }}>
      <div style={{ height: h, background: 'var(--bg-hover)', borderRadius: '99px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(percent, 100)}%`, background: color, borderRadius: '99px', transition: 'width 0.5s' }} />
      </div>
    </div>
  );
}

function GatewayConfig({ gateway, onSave }) {
  const fields = GATEWAY_FIELDS[gateway.name] || [];
  const [form, setForm] = useState({ is_active: gateway.is_active, is_test: gateway.is_test, config: {} });
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await billingAPI.upsertGateway(gateway.name, form);
      toast.success(`${gateway.name} configurado ✓`);
      onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  return (
    <div style={{ padding: '16px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontWeight: '700', fontSize: '15px', textTransform: 'capitalize' }}>{gateway.name}</span>
          <Badge color={gateway.is_active ? '#10b981' : '#6b7280'}>{gateway.is_active ? 'Activa' : 'Inactiva'}</Badge>
          {gateway.is_test && <Badge color="#f59e0b">Sandbox</Badge>}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_test} onChange={e => setForm(f => ({ ...f, is_test: e.target.checked }))} />
            Sandbox
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
            Activar
          </label>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
        {fields.map(field => (
          <div key={field.key}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>{field.label}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={field.secret && !showSecrets[field.key] ? 'password' : 'text'}
                placeholder={gateway.config_preview?.[field.key] || `Ingresa ${field.label.toLowerCase()}`}
                onChange={e => setForm(f => ({ ...f, config: { ...f.config, [field.key]: e.target.value } }))}
                style={{ width: '100%', padding: '8px 36px 8px 10px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box' }}
              />
              {field.secret && (
                <button onClick={() => setShowSecrets(s => ({ ...s, [field.key]: !s[field.key] }))}
                  style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px' }}>
                  {showSecrets[field.key] ? '🙈' : '👁'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Button size="sm" loading={saving} onClick={handleSave}>Guardar configuración</Button>
    </div>
  );
}

function PlanEditor({ plans, onSave }) {
  const [editing, setEditing] = useState(null); // plan id being edited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      ai_credit_usd: p.ai_credit_usd,
      max_posts: p.max_posts,
      max_tokens_monthly: p.max_tokens_monthly ?? -1,
      max_networks: p.max_networks,
      price_monthly: p.price_monthly,
      price_yearly: p.price_yearly,
      regular_price_monthly: p.regular_price_monthly ?? p.price_monthly,
      regular_price_yearly: p.regular_price_yearly ?? p.price_yearly,
      discount_percent: p.discount_percent ?? 0,
    });
  };

  const handleFieldChange = (key, value) => {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (['regular_price_monthly', 'regular_price_yearly', 'discount_percent'].includes(key)) {
        const disc = key === 'discount_percent' ? value : (next.discount_percent || 0);
        const regM = key === 'regular_price_monthly' ? value : (next.regular_price_monthly || 0);
        const regY = key === 'regular_price_yearly' ? value : (next.regular_price_yearly || 0);
        next.price_monthly = Math.round(regM * (1 - disc / 100));
        next.price_yearly = Math.round(regY * (1 - disc / 100));
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await billingAPI.updatePlan(editing, form);
      toast.success('Plan actualizado');
      setEditing(null);
      onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {plans.map(p => (
        <Card key={p.id} style={{ borderLeft: `3px solid ${p.is_active ? '#10b981' : '#6b7280'}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: '700', fontSize: '15px' }}>{p.name}</span>
                <Badge color={p.is_active ? '#10b981' : '#6b7280'}>{p.is_active ? 'Activo' : 'Inactivo'}</Badge>
              </div>
              {editing === p.id ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
                  {[
                    { key: 'ai_credit_usd', label: '🤖 Crédito IA (USD)', type: 'number', step: '0.01' },
                    { key: 'max_posts', label: '📤 Máx. posts/mes (-1 = ∞)', type: 'number', step: '1' },
                  { key: 'max_tokens_monthly', label: '⚡ Tokens/mes (-1 = ∞)', type: 'number', step: '1000' },
                    { key: 'max_networks', label: '🌐 Máx. redes', type: 'number', step: '1' },
                    { key: 'regular_price_monthly', label: '💰 Precio de lista mensual', type: 'number', step: '1000' },
                    { key: 'regular_price_yearly', label: '💰 Precio de lista anual', type: 'number', step: '1000' },
                    { key: 'discount_percent', label: '🏷️ % Descuento activo', type: 'number', step: '1' },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                      <input
                        type={f.type} step={f.step}
                        value={form[f.key]}
                        onChange={e => handleFieldChange(f.key, parseFloat(e.target.value) || 0)}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
                      />
                    </div>
                  ))}
                  <div style={{ gridColumn: '1 / -1', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-hover)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    💳 Precio real que se cobrará (calculado): <strong>${form.price_monthly}/mes</strong> · <strong>${form.price_yearly}/año</strong>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span>💰 ${p.price_monthly}/mes · ${p.price_yearly}/año{p.discount_percent > 0 ? ` (-${p.discount_percent}% de $${p.regular_price_monthly})` : ''}</span>
                  <span>🤖 ${p.ai_credit_usd} crédito IA</span>
                  <span>📤 {p.max_posts === -1 ? 'Ilimitados' : p.max_posts} posts/mes</span>
                  <span>⚡ {p.max_tokens_monthly === -1 ? 'Tokens ∞' : `${(p.max_tokens_monthly || 0).toLocaleString()} tokens/mes`}</span>
                  <span>🌐 {p.max_networks} redes</span>
                  <span>👥 {p.max_users} usuario{p.max_users > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              {editing === p.id ? (
                <>
                  <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>Cancelar</Button>
                  <Button size="sm" loading={saving} onClick={handleSave}>Guardar</Button>
                </>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => startEdit(p)}>✏️ Editar</Button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function BillingAdminPage() {
  const [tab, setTab]         = useState('overview');
  const [overview, setOverview] = useState(null);
  const [users, setUsers]     = useState([]);
  const [providers, setProviders] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activating, setActivating] = useState(false);
  const [newPlan, setNewPlan] = useState('');
  const [newCycle, setNewCycle] = useState('monthly');

  const load = async () => {
    setLoading(true);
    try {
      const [ov, us, pr, gw, pl] = await Promise.all([
        billingAPI.adminOverview(),
        billingAPI.adminUsers(),
        billingAPI.adminProviderCosts(),
        billingAPI.listGateways(),
        billingAPI.listPlans(),
      ]);
      setOverview(ov.data); setUsers(us.data);
      setProviders(pr.data); setGateways(gw.data);
      setPlans(pl.data);
    } catch (e) {
      toast.error('Error cargando datos');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleActivate = async () => {
    if (!selectedUser || !newPlan) return;
    setActivating(true);
    try {
      await billingAPI.adminActivateUser(selectedUser.user_id, newPlan, newCycle);
      toast.success('Plan activado ✓');
      setSelectedUser(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
    setActivating(false);
  };

  const TABS = [
    { id: 'overview',  label: '📊 Resumen' },
    { id: 'users',     label: '👥 Usuarios' },
    { id: 'providers', label: '🤖 Proveedores IA' },
    { id: 'gateways',  label: '💳 Pasarelas' },
    { id: 'plans',     label: '📋 Planes' },
  ];

  const fmt = v => v > 999 ? (v/1000).toFixed(1)+'K' : (v||0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', whiteSpace: 'nowrap',
            background: tab === t.id ? 'var(--bg-card)' : 'transparent',
            color: tab === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
            fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: tab === t.id ? '700' : '500',
            cursor: 'pointer', boxShadow: tab === t.id ? 'var(--shadow-sm)' : 'none',
          }}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: '80px', borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      ) : (
        <>
          {/* OVERVIEW */}
          {tab === 'overview' && overview && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* KPIs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
                {[
                  { label: 'Ingresos totales', val: `$${overview.total_revenue_usd.toFixed(2)}`, icon: '💰', color: '#10b981' },
                  { label: 'Costo IA total',   val: `$${overview.total_ai_cost_usd.toFixed(4)}`, icon: '🤖', color: '#7c3aed' },
                  { label: 'Margen',           val: `$${overview.margin_usd.toFixed(2)}`,        icon: '📈', color: overview.margin_usd >= 0 ? '#10b981' : '#ef4444' },
                  { label: 'Tokens totales',   val: fmt(overview.total_tokens),                  icon: '⚡', color: '#3b82f6' },
                  { label: 'Subs activas',     val: overview.active_subscriptions,               icon: '👥', color: '#f59e0b' },
                  { label: 'Total usuarios',   val: overview.total_subscriptions,                icon: '📋', color: '#6b7280' },
                ].map(k => (
                  <div key={k.label} style={{ padding: '16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', borderLeft: `3px solid ${k.color}` }}>
                    <div style={{ fontSize: '20px', marginBottom: '6px' }}>{k.icon}</div>
                    <div style={{ fontWeight: '800', fontSize: '20px', color: k.color }}>{k.val}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Por plan + por proveedor */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                <Card>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '14px', marginBottom: '14px' }}>Usuarios por plan</h3>
                  {Object.entries(overview.by_plan).map(([plan, count]) => (
                    <div key={plan} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
                      <span>{plan}</span><span style={{ fontWeight: '700' }}>{count} usuarios</span>
                    </div>
                  ))}
                </Card>
                <Card>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '14px', marginBottom: '14px' }}>Costo por proveedor IA</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={overview.by_provider}>
                      <XAxis dataKey="provider" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                      <Tooltip formatter={v => [`$${v.toFixed(6)}`, 'Costo USD']} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '12px' }} />
                      <Bar dataKey="cost_usd" radius={[4,4,0,0]}>
                        {overview.by_provider.map((e, i) => <Cell key={i} fill={PROVIDER_COLOR[e.provider] || '#888'} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>
            </div>
          )}

          {/* USUARIOS */}
          {tab === 'users' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Modal activar plan */}
              {selectedUser && (
                <div style={{ padding: '16px', background: 'var(--bg-card)', border: '1px solid #7c3aed', borderRadius: 'var(--radius-lg)' }}>
                  <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: '700', marginBottom: '12px' }}>
                    Asignar plan a {selectedUser.user_name}
                  </h4>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <select value={newPlan} onChange={e => setNewPlan(e.target.value)}
                      style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none' }}>
                      <option value="">Seleccionar plan...</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.name} — ${p.price_monthly}/mes</option>)}
                    </select>
                    <select value={newCycle} onChange={e => setNewCycle(e.target.value)}
                      style={{ padding: '8px 12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: '13px', fontFamily: 'var(--font-body)', outline: 'none' }}>
                      <option value="monthly">Mensual</option>
                      <option value="yearly">Anual</option>
                    </select>
                    <Button size="sm" loading={activating} onClick={handleActivate} disabled={!newPlan}>Activar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedUser(null)}>Cancelar</Button>
                  </div>
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Usuario', 'Plan', 'Estado', 'Crédito IA', 'Tokens', 'Posts', 'Vence', 'Acciones'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-muted)', fontWeight: '700', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u.user_id} className={`animate-fadeIn delay-${Math.min(i+1,5)}`} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '600' }}>{u.user_name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{u.user_email}</div>
                        </td>
                        <td style={{ padding: '12px' }}><Badge color="#7c3aed">{u.plan}</Badge></td>
                        <td style={{ padding: '12px' }}><Badge color={u.status === 'active' ? '#10b981' : '#6b7280'}>{u.status}</Badge></td>
                        <td style={{ padding: '12px', minWidth: '160px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1 }}><CreditBar percent={u.credit_percent} size="sm" /></div>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: u.credit_percent >= 95 ? '#ef4444' : u.credit_percent >= 70 ? '#f59e0b' : '#10b981', whiteSpace: 'nowrap' }}>
                              {u.credit_percent}%
                            </span>
                          </div>
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                            ${u.ai_credit_used.toFixed(4)} / ${u.ai_credit_limit.toFixed(2)}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}><div style={{ fontSize: '13px', fontWeight: '600' }}>{(u.tokens_used||0).toLocaleString()}</div><div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{u.tokens_limit === -1 ? '∞ ilimitado' : 'de ' + (u.tokens_limit||0).toLocaleString()}</div><div style={{ fontSize: '10px', color: '#10b981', marginTop: '2px' }}>${(u.ai_credit_used||0).toFixed(6)} USD</div></td><td style={{ padding: '12px' }}>{u.posts_used}</td>
                        <td style={{ padding: '12px', fontSize: '12px', color: u.days_remaining <= 3 ? '#ef4444' : 'var(--text-muted)' }}>
                          {u.period_end ? new Date(u.period_end + 'Z').toLocaleDateString('es', { day: 'numeric', month: 'short' }) : '—'}
                          {u.days_remaining > 0 && <span style={{ fontSize: '10px', display: 'block' }}>{u.days_remaining}d restantes</span>}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <Button size="sm" variant="secondary" onClick={() => { setSelectedUser(u); setNewPlan(''); }}>
                            Cambiar plan
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* PROVEEDORES IA */}
          {tab === 'providers' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {providers.map(p => (
                <Card key={p.provider} style={{ borderLeft: `3px solid ${PROVIDER_COLOR[p.provider] || '#888'}` }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <span style={{ fontWeight: '700', fontSize: '15px', textTransform: 'capitalize' }}>{p.name}</span>
                        <Badge color={p.is_active ? '#10b981' : '#6b7280'}>{p.is_active ? '● Activo' : '○ Inactivo'}</Badge>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{p.model}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '18px', color: PROVIDER_COLOR[p.provider] || '#888' }}>${p.local_cost_usd.toFixed(6)}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Costo calculado local</div>
                        </div>
                        <div>
                          <div style={{ fontWeight: '800', fontSize: '18px' }}>{fmt(p.local_tokens)}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Tokens totales</div>
                        </div>
                        {p.pricing && (p.pricing.input || p.pricing.output) && (
                          <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '6px' }}>⚡ Tarifas OpenRouter</div>
                            <div style={{ display: 'flex', gap: '16px' }}>
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#10b981' }}>${p.pricing.input?.toFixed(6)}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>por 1K input</div>
                              </div>
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#f59e0b' }}>${p.pricing.output?.toFixed(6)}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>por 1K output</div>
                              </div>
                            </div>
                            {p.pricing_updated_at && <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>🔄 {new Date(p.pricing_updated_at).toLocaleDateString('es-CO', {day:'2-digit',month:'short',year:'numeric'})}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                    {p.api_data && !p.api_data.error && (() => {
                      const d = p.api_data;
                      // DeepSeek: balance_infos array
                      if (d.balance_infos) {
                        return (
                          <div style={{ padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', minWidth: '160px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '8px' }}>💰 Saldo disponible</div>
                            {d.balance_infos.map((b, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' }}>
                                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{b.currency}</span>
                                <span style={{ fontSize: '14px', fontWeight: '800', color: '#10b981' }}>${parseFloat(b.total_balance).toFixed(4)}</span>
                              </div>
                            ))}
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>{d.is_available ? '✅ API disponible' : '❌ API no disponible'}</div>
                          </div>
                        );
                      }
                      // OpenAI: usage data
                      if (d.data !== undefined || d.total_usage !== undefined) {
                        const usd = d.total_usage ? (d.total_usage / 100).toFixed(4) : '0.0000';
                        return (
                          <div style={{ padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', minWidth: '160px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '8px' }}>💰 Uso hoy (USD)</div>
                            <div style={{ fontSize: '18px', fontWeight: '800', color: '#10b981' }}>${usd}</div>
                          </div>
                        );
                      }
                      // Gemini: status ok
                      if (d.status === 'ok') {
                        return (
                          <div style={{ padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', minWidth: '160px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '6px' }}>Estado API</div>
                            <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '700' }}>✅ Conectado</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '4px' }}>Gemini no expone saldo vía API</div>
                          </div>
                        );
                      }
                      // Note / Groq / others
                      if (d.note) {
                        return (
                          <div style={{ padding: '10px 14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', minWidth: '160px' }}>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>ℹ️ {d.note}</div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {p.error && <Badge color="#ef4444">Error: {p.error}</Badge>}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* PASARELAS */}
          {tab === 'gateways' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                Configura las pasarelas de pago. Las credenciales se guardan de forma segura y los campos marcados con 🔒 se ocultan después de guardar.
              </p>
              {gateways.map(gw => (
                <GatewayConfig key={gw.name} gateway={gw} onSave={load} />
              ))}
            </div>
          )}

          {/* PLANES */}
          {tab === 'plans' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                Los planes se crean automáticamente al iniciar el servidor. Puedes editarlos aquí.
              </p>
              <PlanEditor plans={plans} onSave={load} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
